import { VisitFormDialog } from '@features/visits/components/visit-form/visit-form-dialog';
import { CreateVisitType } from '@features/visits/entity';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { ImperativeFormDialogRefType } from '@shared/components/UserFormDialog';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import DayTimelineView from './DayTimelineView';
import { VisitDailyOverviewFastOrderForm } from './visit-daily-overview-fast-order-form';
import DayEditDialog from '@features/profile/work-schedule/components/DayEditDialog';
import { useDayScheduleEdit } from '@features/profile/work-schedule/hooks';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { Edit } from '@mui/icons-material';

const VisitDailyOverviewDiagramDialog = (props: {
  onClose: () => void;
  selectedTimelineDate: Date | null;
}) => {
  const dialogRef = useRef<ImperativeFormDialogRefType | null>(null);
  const methods = useForm<CreateVisitType>();
  const { t } = useIntlTranslations('visits');
  const visitFormRef = useRef<ImperativeFormDialogRefType | null>(null);
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);

  const selectedDayjs = props.selectedTimelineDate ? dayjs(props.selectedTimelineDate) : null;
  const { currentHoliday, currentDayOverride, defaultDaySchedule, handleDaySave } =
    useDayScheduleEdit(selectedDayjs);

  return (
    <>
      <Dialog open={!!props.selectedTimelineDate} onClose={props.onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Typography
              variant="h6"
              fontSize="0.675rem"
              sx={{
                flex: 1,
                minWidth: 0,
                lineHeight: 1.15,
                wordBreak: 'break-word',
              }}
            >
              {selectedDayjs?.format('dddd, D. MMMM YYYY')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Edit sx={{ width: '0.8rem' }} />}
                onClick={() => setHolidayDialogOpen(true)}
                sx={{ fontSize: 9, py: 0.25, px: 1, minWidth: 0 }}
              >
                {t('quickAddHoliday')}
              </Button>
              <IconButton onClick={props.onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {props.selectedTimelineDate && (
            <DayTimelineView
              date={props.selectedTimelineDate}
              onSlotClick={dateTime => {
                methods.setValue('date', dayjs(dateTime).toDate());
                dialogRef.current?.open();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      <VisitDailyOverviewFastOrderForm methods={methods} dialogRef={dialogRef} />
      <VisitFormDialog dialogRef={visitFormRef} />
      <DayEditDialog
        open={holidayDialogOpen}
        onClose={() => setHolidayDialogOpen(false)}
        selectedDate={selectedDayjs}
        currentHoliday={currentHoliday}
        currentDayOverride={currentDayOverride}
        defaultDaySchedule={defaultDaySchedule}
        onSave={(holiday, dayOverride) => {
          handleDaySave(holiday, dayOverride);
          setHolidayDialogOpen(false);
        }}
      />
    </>
  );
};

export default VisitDailyOverviewDiagramDialog;
